import React, { useContext, useEffect, useState } from 'react'
import { Button, Spin } from 'antd';
import { FullscreenExitOutlined, FullscreenOutlined, LoadingOutlined, CaretUpFilled, CaretDownFilled, MinusOutlined } from '@ant-design/icons';
import CloudyDay from '../../assets/img/cloudy-day.svg'
import Overcast from '../../assets/img/overcast-clouds.png'
import Sunny from '../../assets/img/sunny.svg'
import { ContextLayout } from '../../utility/LayoutContext.js';
import moment from 'moment';
import './board.css'
import classNames from 'classnames';
import { useParams } from 'react-router-dom';
import { useGetData } from '../../hooks/serviceGetways';
import useLiveDataFetcher from './getLiveData';
import { priceFormatter } from '../../utility/string.js';

const flagMap = {
    "USD": "https://hatscripts.github.io/circle-flags/flags/us.svg",
    "EUR": "https://hatscripts.github.io/circle-flags/flags/eu.svg",
    "GBP": "https://hatscripts.github.io/circle-flags/flags/gb.svg",
    "CHF": "https://hatscripts.github.io/circle-flags/flags/ch.svg",
    "SAR": "https://hatscripts.github.io/circle-flags/flags/sa.svg",
};

const bgMap = {
    // DÖVİZ
    "USD": "/bgs/usd.png",
    "EUR": "/bgs/eur.png",
    "EUR/USD": "/bgs/parity.png",
    "GBP": "/bgs/gbp.png",
    "CHF": "/bgs/chf.png",
    "SAR": "/bgs/sar.png",
    
    // ALTIN
    "ONS": "/bgs/ons.png",
    "HAS": "/bgs/gold.png",
    "GRAM": "/bgs/gram.png",
    "ÇEYREK": "/bgs/ceyrek.png",
    "YARIM": "/bgs/ceyrek.png",
    "REŞAT": "/bgs/resat.png",
};

const getBgStyle = (currency) => {
    let img = bgMap[currency.boardName];
    if (!img) {
        if (currency.type === "emtia") img = bgMap["GRAM"];
        else img = bgMap["EUR"];
    }
    
    return {
        backgroundImage: `linear-gradient(to right, rgba(15, 23, 42, 0.3) 0%, rgba(15, 23, 42, 0.55) 15%, rgba(15, 23, 42, 0.8) 25%, rgba(15, 23, 42, 0.95) 31%, #0f172a 34%, #0f172a 100%), url("${img}")`,
        backgroundSize: '100% 100%, 35% auto',
        backgroundPosition: 'left center, left center',
        backgroundRepeat: 'no-repeat, no-repeat',
        borderRadius: '1.5vh'
    };
};

const SmallGoldCoin = ({ title }) => {
    let main = "Au";
    if (!title) title = ""; // Safety
    if (title.toUpperCase().includes("ONS")) main = "XAU";
    else if (title.toUpperCase().includes("HAS") || title.toUpperCase().includes("995")) main = "995";
    else if (title.toUpperCase().includes("GRAM")) main = "GR";
    else if (title.toUpperCase().includes("ÇEYREK")) main = "1/4";
    else if (title.toUpperCase().includes("YARIM")) main = "1/2";
    else if (title.toUpperCase().includes("REŞAT")) main = "RŞT";
    else if (title.toUpperCase().includes("TAM") || title.toUpperCase().includes("ATA")) main = "1/1";
    
    return (
        <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" style={{width: '4.8vh', height: '4.8vh', borderRadius: '50%', boxShadow: '0 2px 6px rgba(0,0,0,0.5)', marginLeft: '1vw'}}>
            <linearGradient id="gold" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FFF176" />
                <stop offset="50%" stopColor="#FFCA28" />
                <stop offset="100%" stopColor="#F57F17" />
            </linearGradient>
            <circle cx="50" cy="50" r="50" fill="url(#gold)" />
            <circle cx="50" cy="50" r="42" fill="transparent" stroke="#FFE082" strokeWidth="2.5" />
            <text x="50" y="60" fontFamily="'Inter', sans-serif" fontWeight="900" fontSize={main.length > 2 ? "28" : "36"} fill="#78350f" textAnchor="middle">{main}</text>
        </svg>
    );
};

const ParityIcon = () => (
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" style={{width: '4.8vh', height: '4.8vh', borderRadius: '50%', boxShadow: '0 2px 6px rgba(0,0,0,0.5)', marginLeft: '1vw', backgroundColor: '#f8fafc'}}>
        <text x="32" y="45" fontFamily="'Inter', sans-serif" fontWeight="900" fontSize="38" fill="#2563eb" textAnchor="middle">€</text>
        <path d="M 20,80 L 80,20" stroke="#94a3b8" strokeWidth="4" />
        <text x="68" y="85" fontFamily="'Inter', sans-serif" fontWeight="900" fontSize="38" fill="#16a34a" textAnchor="middle">$</text>
    </svg>
);

const SmallFlag = ({ code, logo }) => {
    return <img src={flagMap[code] || logo} style={{width: '4.8vh', height: '4.8vh', borderRadius: '50%', objectFit: 'cover', boxShadow: '0 2px 6px rgba(0,0,0,0.5)', marginLeft: '1vw'}} alt={code} onError={(e) => { e.target.style.display = 'none'; }} />
}

const AllCurrencyList = (props) => {
    const { settings, liveDatas } = useContext(ContextLayout)
    const { id } = useParams()
    const getData = useGetData()
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [clockState, setClockState] = useState(moment().toDate());
    const [logo, setLogo] = useState(null);
    const [weather, setWeather] = useState(null);

    const { isLeader, clientId } = useLiveDataFetcher(settings?.currencies || []);

    const api = {
        key: "fbc0e225b268727b4ba01f6840d43112",
        base: "https://api.openweathermap.org/data/2.5/"
    }

    const onClickHandler = () => {
        fetch(`${api.base}weather?q=Çorum&units=metric&lang=tr&APPID=${api.key}`)
            .then(res => res.json())
            .then(result => {
                setWeather({ 
                    temp: Math.round(result?.main?.temp), 
                    desc: result?.weather[0]?.description,
                    iconCode: result?.weather[0]?.icon
                });
            }).catch(e => {
                setWeather({ temp: 15, desc: "Parçalı Bulutlu", iconCode: "02d" });
            });
    }

    useEffect(() => {
        onClickHandler();
        if (id) {
            getData("boards/" + id, {}).then(({ result, resultMessage }) => {
                if (result) setLogo(result.logo);
            })
        }
        
        const timer = setInterval(() => {
            setClockState(moment().toDate());
        }, 1000);

        const handleFsChange = () => setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement));
        const events = ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'];
        events.forEach(e => document.addEventListener(e, handleFsChange));

        return () => {
            clearInterval(timer);
            events.forEach(e => document.removeEventListener(e, handleFsChange));
        };
    }, [id])

    const toggleFullScreen = () => {
        const docEl = document.documentElement;
        const isFs = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement);

        if (!isFs) {
            try {
                if (docEl.requestFullscreen) {
                    const promise = docEl.requestFullscreen();
                    if (promise) promise.catch(() => {
                        if (document.body.requestFullscreen) document.body.requestFullscreen();
                    });
                }
                else if (docEl.webkitRequestFullscreen) docEl.webkitRequestFullscreen();
                else if (docEl.mozRequestFullScreen) docEl.mozRequestFullScreen();
                else if (docEl.msRequestFullscreen) docEl.msRequestFullscreen();
            } catch (err) {
                console.error("FS Error:", err);
            }
        } else {
            try {
                if (document.exitFullscreen) document.exitFullscreen();
                else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
                else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
                else if (document.msExitFullscreen) document.msExitFullscreen();
            } catch (err) {
                console.error("FS Exit Error:", err);
            }
        }
    };


    if (!liveDatas.length || !settings || !logo)
        return <div className="vw-100 vh-100 d-flex justify-content-center align-items-center" style={{background: '#020617'}}><Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 64, color: '#38bdf8' }} spin />} ></Spin></div>

    return (
        <>
            <section className="currency-main">
                <div className="currency-header">
                    <div className="currency-weather">
                        {weather?.iconCode ? (
                            <img 
                                src={`https://basmilius.github.io/weather-icons/production/fill/openweathermap/${weather.iconCode}.svg`} 
                                alt="weather" 
                                style={{
                                    height: '9vh', 
                                    width: '9vh', 
                                    objectFit: 'contain', 
                                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.4))'
                                }} 
                                onError={(e) => { 
                                    e.target.onerror = null; 
                                    e.target.src = `https://openweathermap.org/img/wn/${weather.iconCode}@2x.png`; 
                                }}
                            />
                        ) : null}
                        <div className="currency-weather-text">
                            <span className="currency-weather-heat">{weather?.temp}°C</span>
                            <span className="location" style={{textTransform: 'capitalize'}}>{weather?.desc}</span>
                        </div>
                    </div>
                    <div className="currency-logo">
                        <img src={logo.url} alt="Logo"></img>
                    </div>
                    <div className="currency-date">
                        <span className="currency-time">
                            {moment(clockState).format("HH")}
                            <span className="blink">:</span>
                            {moment(clockState).format("mm")}
                            <span className="blink">:</span>
                            {moment(clockState).format("ss")}
                        </span>
                        <span className="currency-day">{moment(clockState).format("DD.MM.YYYY")}</span>
                    </div>
                </div>
                <div className="currency-container">
                    <div className="currency-detail">

                        {settings.currencies && settings.currencies.filter(c => c.type === "doviz" && c.viewBoard).map(currency => {
                            const live = liveDatas.find(liveData => currency.key === liveData.key)
                            const diffStatus = live?.differance == 0 ? "stabil" : live?.differance > 0 ? "up" : "down";
                            
                            return (
                                <div key={currency._id} className={classNames("currency-cell", diffStatus)} style={getBgStyle(currency)}>
                                    <div className="currency-identity">
                                        <div className="currency-names">
                                            <span className="currency-title">{currency.boardName}</span>
                                            <span className="currency-label">{currency.title}</span>
                                        </div>
                                        <div className="currency-logo-row">
                                            <div className={classNames("currency-badge-inline", diffStatus)}>
                                                {diffStatus === 'up' && <CaretUpFilled style={{fontSize: '2vh'}} />}
                                                {diffStatus === 'down' && <CaretDownFilled style={{fontSize: '2vh'}} />}
                                                {diffStatus === 'stabil' && <MinusOutlined style={{fontSize: '2vh'}} />}
                                                <span>%{String(live?.differance || "").replace('-', '')}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="currency-price-row">
                                        <div className="price-box">
                                            <span className="price-val">{priceFormatter(live?.salesPrice,3)}</span>
                                        </div>
                                        <div className="price-box">
                                            <span className="price-val">{priceFormatter(live?.purchasePrice,3)}</span>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                    <div className="currency-detail">

                        {settings.currencies && settings.currencies.filter(c => c.type === "emtia" && c.viewBoard).map(currency => {
                            const live = liveDatas.find(liveData => currency.key === liveData.key)
                            const diffStatus = live?.differance == 0 ? "stabil" : live?.differance > 0 ? "up" : "down";

                            return (
                                <div key={currency._id} className={classNames("currency-cell", diffStatus)} style={getBgStyle(currency)}>
                                    <div className="currency-identity">
                                        <div className="currency-names">
                                            <span className="currency-title">{currency.boardName}</span>
                                            <span className="currency-label">{currency.title}</span>
                                        </div>
                                        <div className="currency-logo-row">
                                            <div className={classNames("currency-badge-inline", diffStatus)}>
                                                {diffStatus === 'up' && <CaretUpFilled style={{fontSize: '2vh'}} />}
                                                {diffStatus === 'down' && <CaretDownFilled style={{fontSize: '2vh'}} />}
                                                {diffStatus === 'stabil' && <MinusOutlined style={{fontSize: '2vh'}} />}
                                                <span>%{String(live?.differance || "").replace('-', '')}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="currency-price-row">
                                        <div className="price-box">
                                            <span className="price-val">{priceFormatter(live?.salesPrice,2)}</span>
                                        </div>
                                        <div className="price-box">
                                            <span className="price-val">{priceFormatter(live?.purchasePrice,2)}</span>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </section>
            <Button className="fullscreen-btn" onClick={toggleFullScreen} shape="circle">
                {isFullscreen ? <FullscreenExitOutlined style={{fontSize: '1.2rem'}} /> : <FullscreenOutlined style={{fontSize: '1.2rem'}} />}
            </Button>
        </>
    );
}
export default AllCurrencyList
